import SaveWithShortCutButton from '$components/SaveWithShortCutButton'
import { usePasswordPolicyService } from '$hooks/services'
import { Checkbox } from '@genie-fintech/ui/components'
import { useCallback, useState } from 'react'
import {
  TField,
  PASSWORD_POLICY_CONFIG,
  schema,
  replaceTempString,
  PASSWORD_POLICY_NAME
} from './constants'
import { BaseText, Password } from '@genie-fintech/ui/components/fields'
import FooterAction from '$components/FooterAction'
import { useBreadcrumb } from '$components/Breadcrumb/hooks/useBreadcrumb'
import {
  container,
  indicator,
  innerContainer,
  item,
  labelStyle,
  previewContainer,
  rulesContainer
} from './styles.css'
import { trueOrUndefined } from '@genie-fintech/ui/functions'
import { useIsMounted } from '@genie-fintech/ui/hooks'
import { description, flexColumn, flexRow } from '$styles/common.css'
import { title } from '@genie-fintech/ui/style/typography'
import { Controller, useController, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import RegexParser from 'regex-parser'
import PasswordRules from '../../components/PasswordRules'
import { MAX_PWD_LENGTH, MIN_PWD_LENGTH } from '$app/constants/common'
import { TPasswordPolicyPayload } from '$services/api'
import Loading from '$components/Loading'
import { COMPANY_NAME } from '$constants/themeTexts'

const passwordStrength = ['weak', 'good', 'strong'] as const

const PasswordPolicy = () => {
  const [testPassword, setTestPassword] = useState('')

  const isMounted = useIsMounted()

  const {
    fetchPasswordPolicyAsync,
    fetchingPasswordPolicy,
    passwordPolicy,
    updatePasswordPolicyAsync,
    updatingPasswordPolicy
  } = usePasswordPolicyService()

  const methods = useForm<TField>({
    resolver: zodResolver(schema),
    defaultValues: async () => {
      const { data } = await fetchPasswordPolicyAsync()
      return data.reduce((acc, cur) => {
        const { name, is_enabled, min_length } = cur
        if (name === 'minimum_characters') {
          acc[name as keyof TField] = {
            is_enabled,
            min_length: `${min_length ?? ''}`
          }
        } else {
          acc[name as keyof TField] = {
            is_enabled,
            min_length: `${min_length ?? ''}`
          }
        }

        return acc
      }, {} as TField)
    }
  })
  const { control, watch, handleSubmit, reset, formState } = methods

  const { field: minLengthField, fieldState: minLengthFieldState } =
    useController({ name: `minimum_characters.min_length`, control })

  useBreadcrumb([{ name: 'Password Policy' }])

  const currentStrength: (typeof passwordStrength)[number] = (() => {
    const total = passwordPolicy.length
    const totalChecked = Object.values(watch()).filter(d => d.is_enabled).length
    const pivot = Math.ceil(total / 2)
    if (totalChecked < pivot) return 'weak'
    if (totalChecked < total) return 'good'
    return 'strong'
  })()

  const checkedPolicies = passwordPolicy.filter(p => {
    return watch(`${p.name}.is_enabled`)
  })

  const onSubmit = useCallback(
    (values: TField) => {
      const policies: TPasswordPolicyPayload[] = Object.entries(values).reduce(
        (acc, [key, value]) => {
          const { is_enabled, min_length } = value
          const policy: TPasswordPolicyPayload = {
            name: key as PASSWORD_POLICY_NAME,
            is_enabled: is_enabled,
            min_length: min_length ? +min_length : null
          }
          return [...acc, policy]
        },
        [] as TPasswordPolicyPayload[]
      )

      updatePasswordPolicyAsync({ policies }).then(() => {
        reset(values)
      })
    },
    [updatePasswordPolicyAsync, reset]
  )

  if (fetchingPasswordPolicy) return <Loading />

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={container}
      data-ready={trueOrUndefined(isMounted)}
    >
      <article className={innerContainer}>
        <article className={flexColumn} style={{ gap: 4 }}>
          <h2 className={title.two}>Password Policy</h2>
          <p className={description}>
            Password strength is an important consideration when using passwords
            for {COMPANY_NAME} service. A strong password policy will make it
            difficult, if not improbable, for someone to guess a password
            through either manual or automated means.
          </p>
        </article>

        <article className={flexColumn} style={{ gap: 16 }}>
          <article className={flexColumn}>
            <p className={title.six}>Strength:</p>
            <article className={flexRow} style={{ gap: 16 }}>
              {passwordStrength.map(strength => {
                return (
                  <article
                    key={strength}
                    className={item({ strength })}
                    data-disabled={trueOrUndefined(
                      currentStrength !== strength
                    )}
                  >
                    <span className={indicator} />
                    <span>{strength}</span>
                  </article>
                )
              })}
            </article>
          </article>

          <article className={rulesContainer}>
            {passwordPolicy
              .sort((a, b) => a.id - b.id)
              .map(p => {
                const { label } = PASSWORD_POLICY_CONFIG[p.name] ?? {}
                return (
                  <Controller
                    key={p.name}
                    name={`${p.name}.is_enabled`}
                    control={control}
                    render={({ field: { value, onChange } }) => {
                      return (
                        <article
                          className={flexRow}
                          style={{ gap: 8, padding: '8px 0' }}
                        >
                          <Checkbox
                            boxProps={{
                              checked: value,
                              onCheckedChange: onChange
                            }}
                          />
                          {p.name !== 'minimum_characters' && (
                            <p className={labelStyle}>{label}</p>
                          )}

                          {p.name === 'minimum_characters' && (
                            <article className={flexRow} style={{ gap: 4 }}>
                              <span className={labelStyle}>Must have</span>
                              <BaseText
                                inputProps={{
                                  type: 'number',
                                  min: MIN_PWD_LENGTH,
                                  max: MAX_PWD_LENGTH,
                                  value: minLengthField.value,
                                  onChange: minLengthField.onChange
                                }}
                                disabled={!value}
                                error={!!minLengthFieldState.error}
                                containerProps={{ style: { width: 50 } }}
                              />
                              <span className={labelStyle}>
                                characters in length
                              </span>
                            </article>
                          )}
                        </article>
                      )
                    }}
                  />
                )
              })}
          </article>

          <article className={previewContainer}>
            <p className={title.three}>Preview</p>
            <article
              className={flexColumn}
              style={{ padding: '8px 0', gap: 10 }}
            >
              <Password
                label="Test Password"
                required
                inputProps={{
                  autoComplete: 'off',
                  value: testPassword,
                  onChange: e => setTestPassword(e.currentTarget.value)
                }}
              />

              {checkedPolicies
                .sort((a, b) => a.id - b.id)
                .map((policy, k) => {
                  const isMinLengthRule = policy.name === 'minimum_characters'

                  const { label: rawLabel, regex: rawRegex } =
                    PASSWORD_POLICY_CONFIG[policy.name] ?? {}

                  const label = isMinLengthRule
                    ? replaceTempString(rawLabel, minLengthField.value)
                    : rawLabel
                  const regex = RegexParser(
                    isMinLengthRule
                      ? replaceTempString(rawRegex, minLengthField.value)
                      : rawRegex
                  )

                  return (
                    <PasswordRules
                      key={k}
                      label={label}
                      isPass={regex.test(testPassword)}
                    />
                  )
                })}
            </article>
          </article>
        </article>
      </article>

      <FooterAction>
        <SaveWithShortCutButton
          disabled={!formState.isDirty}
          loading={updatingPasswordPolicy}
        />
      </FooterAction>
    </form>
  )
}

export default PasswordPolicy
